import React, { useState, useEffect, useMemo } from "react";
import {
  Search, Filter, X, AlertTriangle,
  Users, Ban, Clock, Settings,
  FileText, Calendar, DollarSign, ShoppingBag,
  Phone, Mail, MapPin, User, Star, MessageSquare,
  Download, Trash2, Edit, Eye, CheckCircle, XCircle,
  RefreshCw, Send, Loader2
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchRefundRequests,
  updateRefundRequest,
  deleteRefundRequest,
  useAppDispatch,
  getUserGeneralSetting,
  getAllCurrencies
} from "../../redux";
import { GlobalService, RefundRequestStatusEnum, RefundRequestTypeEnum, NotificationTypeEnum, NotificationPriorityEnum, NotificationStatusEnum, CommissionStatusEnum, CommissionCategoryTypeEnum } from "../../modules";
import { escrowAPI, EscrowStatusEnum } from "../../services/escrowAPI";
import { findCurrencyIdByCode, getDynamicCurrencySymbol } from "../../lib/currencyUtils";
import { createUserNotificationAction } from "../../redux/Actions/UserNotificationActions";
import { fetchCommissions } from "../../redux/Slices/commissionSlice";

const DisputeRequestsPage = () => {
  // Redux hooks
  const dispatch = useAppDispatch();
  const { refundRequests, loading, error, pagination } = useSelector((state: any) => state.refundRequests);
  const isLoading = useSelector((state: any) => state.loader.isLoading);
  const { user, currencies } = useSelector((state: any) => state.auth);
  const { items: commissionRules = [] } = useSelector((state: any) => state.commissions);

  // State management
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [showRequestDetails, setShowRequestDetails] = useState(null);
  const [sortBy, setSortBy] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [filters, setFilters] = useState({
    dateRange: "all",
    amountRange: "all"
  });

  // Fetch commission rules on component mount
  useEffect(() => {
    dispatch(fetchCommissions(undefined));
  }, [dispatch]);

  // Fetch dispute requests on component mount and when filters change
  useEffect(() => {
    fetchDisputeRequests();
  }, [currentPage, statusFilter, typeFilter, sortBy, filters]);

  const fetchDisputeRequests = async () => {
    try {
      const queryParams: any = {
        page: currentPage,
        limit
      };

      // Build filters object
      const filters: any = {};
      // Add statusIds to filters
      if (statusFilter !== "all") {
        filters.statusIds = [statusFilter];
      } else {
        // Default statusIds if no specific status is selected
        // filters.statusIds = [RefundRequestStatusEnum.Rejected.toString()];
      }

      // Add type filter to main query params
      if (typeFilter !== "all") {
        queryParams.type = Number(typeFilter);
      }

      // Add search query
      if (searchQuery && searchQuery.length >= 3) {
        queryParams.keyword = searchQuery;
      }

      // Add sorting parameters
      if (sortBy) {
        switch (sortBy) {
          case "newest":
            queryParams.sort = JSON.stringify([{ orderBy: "createdAt", order: "DESC" }]);
            break;
          case "oldest":
            queryParams.sort = JSON.stringify([{ orderBy: "createdAt", order: "ASC" }]);
            break;
          case "amount_high":
            queryParams.sort = JSON.stringify([{ orderBy: "amount", order: "DESC" }]);
            break;
          case "amount_low":
            queryParams.sort = JSON.stringify([{ orderBy: "amount", order: "ASC" }]);
            break;
          default:
            queryParams.sort = JSON.stringify([{ orderBy: "createdAt", order: "DESC" }]);
        }
      }

      // Add filters to query params if filters object has content
      if (Object.keys(filters).length > 0) {
        queryParams.filters = filters;
      }

      await dispatch(fetchRefundRequests(queryParams));
    } catch (error) {
      console.error("Failed to fetch dispute requests:", error);
    }
  };

  // Handle search with debounce
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Debounced search effect - trigger after 3 characters
  useEffect(() => {
    if (searchQuery.length >= 3 || searchQuery.length === 0) {
      const timeoutId = setTimeout(() => {
        fetchDisputeRequests();
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery]);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle status update
  const handleStatusUpdate = async (requestId, newStatusId) => {
    try {
      const result = await dispatch(updateRefundRequest({
        id: requestId,
        data: { status: { id: newStatusId } }
      }));

      if (updateRefundRequest.fulfilled.match(result)) {
        GlobalService.showToast("success", "Success", "Dispute request status updated successfully!");
        fetchDisputeRequests();
        setShowRequestDetails(null);
      } else {
        GlobalService.showToast("error", "Error", "Failed to update dispute request status.");
      }
    } catch (error) {
      console.error("Error updating dispute request status:", error);
      GlobalService.showToast("error", "Error", "Failed to update dispute request status.");
    }
  };

  // Handle delete request
  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm("Are you sure you want to delete this dispute request? This action cannot be undone.")) {
      return;
    }

    try {
      const result = await dispatch(deleteRefundRequest(requestId));

      if (deleteRefundRequest.fulfilled.match(result)) {
        GlobalService.showToast("success", "Success", "Dispute request deleted successfully!");
        fetchDisputeRequests();
        setShowRequestDetails(null);
      } else {
        GlobalService.showToast("error", "Error", "Failed to delete dispute request.");
      }
    } catch (error) {
      console.error("Error deleting dispute request:", error);
      GlobalService.showToast("error", "Error", "Failed to delete dispute request.");
    }
  };

  // Handle refund payment
  const handleRefundPayment = async (request) => {
    if (!window.confirm(`Are you sure you want to refund $${request.amount?.toFixed(2) || "0.00"} to the customer?`)) {
      return;
    }

    try {
      const requestType = Number(request.type?.id);

      // Only process for Full type refunds
      if (requestType === RefundRequestTypeEnum.Full) {
        const contractId = request.contract?.id;

        if (!contractId) {
          GlobalService.showToast("error", "Error", "Contract ID not found for this refund request.");
          return;
        }

        // Get all escrows for this contract
        const escrows = await escrowAPI.getEscrowsByContract(contractId);

        // Filter for funded escrows only
        const fundedEscrows = escrows.filter(
          (escrow) => Number(escrow.status?.id) === EscrowStatusEnum.funded
        );

        if (fundedEscrows.length === 0) {
          GlobalService.showToast("warning", "Warning", "No funded escrows found for this contract.");
          return;
        }

        // Calculate total funded amount
        const totalFundedAmount = fundedEscrows.reduce((sum, escrow) => sum + (escrow.amount || 0), 0);

        

        const clientId = request.contract?.client?.id;

        // Create new escrow with client ID and status refunded
        const clientEscrow = await escrowAPI.createEscrow({
          contractId: contractId,
          clientId: clientId,
          amount: totalFundedAmount,
          currency: fundedEscrows[0]?.currency?.id?.toString() || "1",
          status: "refunded",
          notes: `Full refund to client - Dispute #${request.id}`,
          metadata: {
            disputeId: request.id,
            resolutionType: "full_refund",
            originalEscrows: fundedEscrows.map(e => e.id)
          }
        });
        await escrowAPI.updateEscrow(clientEscrow.id, { status: "refunded" });

        // Process refund for all funded escrows
        const refundPromises = fundedEscrows.map((escrow) =>
          escrowAPI.refundPayment(escrow.id)
        );
        await Promise.all(refundPromises);

        // Update all funded escrows to disputed status
        const updatePromises = fundedEscrows.map((escrow) =>
          escrowAPI.updateEscrow(escrow.id, { status: "disputed" })
        );
        await Promise.all(updatePromises);

        GlobalService.showToast(
          "success",
          "Success",
          `Successfully refunded ${fundedEscrows.length} escrow(s).`
        );

        // Send notifications to both professional and client
        try {
          const clientId = request.contract?.client?.id;
          const professionalId = request.contract?.professional?.id;
          const amount = request.amount?.toFixed(2) || "0.00";
          const contractTitle = request.contract?.title || "Contract";

          // Notification to client
          if (clientId) {
            await createUserNotificationAction({
              user: { id: clientId },
              type: { id: NotificationTypeEnum["In-App"] },
              priority: { id: NotificationPriorityEnum.High },
              status: { id: NotificationStatusEnum.Unread },
              title: "Refund Processed",
              message: `Your refund of $${amount} for "${contractTitle}" has been processed successfully.`,
              url: "/client/refund-requests"
            });
          }

          // Notification to professional
          if (professionalId) {
            await createUserNotificationAction({
              user: { id: professionalId },
              type: { id: NotificationTypeEnum["In-App"] },
              priority: { id: NotificationPriorityEnum.High },
              status: { id: NotificationStatusEnum.Unread },
              title: "Payment Refunded",
              message: `Payment of $${amount} for "${contractTitle}" has been refunded to the client.`,
              url: "/refund-requests"
            });
          }

          console.log("Notifications sent successfully to both parties");
        } catch (notificationError) {
          console.error("Error sending notifications:", notificationError);
          // Don't fail the whole operation if notifications fail
        }

        // After successful refund, update status to Refunded
        await handleStatusUpdate(request.id, RefundRequestStatusEnum.Refunded);
      } else if (requestType === RefundRequestTypeEnum.Partial) {
        // For partial refunds, keep empty as per requirements
        GlobalService.showToast("info", "Info", "Partial refund functionality is not implemented yet.");
      } else {
        GlobalService.showToast("error", "Error", "Invalid refund request type.");
      }
    } catch (error) {
      console.error("Error processing refund:", error);
      GlobalService.showToast("error", "Error", "Failed to process refund payment: " + (error?.message || "Unknown error"));
    }
  };

  // Handle release payment
  const handleReleasePayment = async (request) => {
    if (!window.confirm(`Are you sure you want to release $${request.amount?.toFixed(2) || "0.00"} to the professional's wallet?`)) {
      return;
    }

    try {
      const professionalId = request.contract?.professional?.id;
      const amount = request.amount;
      const contractId = request.contract?.id;

      if (!professionalId) {
        GlobalService.showToast("error", "Error", "Professional ID not found for this release request.");
        return;
      }

      if (!amount || amount <= 0) {
        GlobalService.showToast("error", "Error", "Invalid amount for release.");
        return;
      }

      if (!contractId) {
        GlobalService.showToast("error", "Error", "Contract ID not found for this release request.");
        return;
      }

      // Get all escrows for this contract
      const escrows = await escrowAPI.getEscrowsByContract(contractId);

      // Filter for funded escrows only
      const fundedEscrows = escrows.filter(
        (escrow) => Number(escrow.status?.id) === EscrowStatusEnum.funded
      );

      if (fundedEscrows.length === 0) {
        GlobalService.showToast("warning", "Warning", "No funded escrows found for this contract.");
        return;
      }

      // Calculate total funded amount
      const totalFundedAmount = fundedEscrows.reduce((sum, escrow) => sum + (escrow.amount || 0), 0);

      // Update all funded escrows to disputed status
      const updatePromises = fundedEscrows.map((escrow) =>
        escrowAPI.updateEscrow(escrow.id, { status: "disputed" })
      );
      await Promise.all(updatePromises);

      // Calculate withdrawal amount after commission
      const withdrawlAmount = calculateWithdrawlAmount(totalFundedAmount, request.contract);

      // Create new escrow with professional and status released
      const professionalEscrow = await escrowAPI.createEscrow({
        contractId: contractId,
        professionalId: professionalId,
        amount: totalFundedAmount,
        withdrawlAmount: withdrawlAmount,
        currency: fundedEscrows[0]?.currency?.id?.toString() || "1",
        status: "released",
        notes: `Full release to professional - Dispute #${request.id} (Withdrawal: $${withdrawlAmount.toFixed(2)} after commission)`,
        metadata: {
          disputeId: request.id,
          resolutionType: "full_release",
          originalEscrows: fundedEscrows.map(e => e.id),
          commissionDeducted: totalFundedAmount - withdrawlAmount
        }
      });
      await escrowAPI.updateEscrow(professionalEscrow.id, { status: "released" });

      GlobalService.showToast(
        "success",
        "Success",
        `Successfully released $${totalFundedAmount.toFixed(2)} to professional. Withdrawal amount: $${withdrawlAmount.toFixed(2)} after commission.`
      );

      // Send notifications to both professional and client
      try {
        const clientId = request.contract?.client?.id;
        const contractTitle = request.contract?.title || "Contract";

        // Notification to professional
        if (professionalId) {
          await createUserNotificationAction({
            user: { id: professionalId },
            type: { id: NotificationTypeEnum["In-App"] },
            priority: { id: NotificationPriorityEnum.High },
            status: { id: NotificationStatusEnum.Unread },
            title: "Payment Released to Wallet",
            message: `Payment for "${contractTitle}" has been released. Gross: $${totalFundedAmount.toFixed(2)}. Withdrawal amount: $${withdrawlAmount.toFixed(2)} after commission.`,
            url: "/refund-requests"
          });
        }

        // Notification to client
        if (clientId) {
          await createUserNotificationAction({
            user: { id: clientId },
            type: { id: NotificationTypeEnum["In-App"] },
            priority: { id: NotificationPriorityEnum.High },
            status: { id: NotificationStatusEnum.Unread },
            title: "Payment Released",
            message: `Payment of $${totalFundedAmount.toFixed(2)} for "${contractTitle}" has been released to the professional.`,
            url: "/client/refund-requests"
          });
        }

        console.log("Notifications sent successfully to both parties");
      } catch (notificationError) {
        console.error("Error sending notifications:", notificationError);
        // Don't fail the whole operation if notifications fail
      }

      // After successful release, update status to Completed
      await handleStatusUpdate(request.id, RefundRequestStatusEnum.Completed);
    } catch (error) {
      console.error("Error releasing payment:", error);
      GlobalService.showToast("error", "Error", "Failed to release payment: " + (error?.message || "Unknown error"));
    }
  };

  // Handle contact parties
  const handleContact = (request) => {
    // Get contact information
    const clientEmail = request.contract?.client?.email;
    const professionalEmail = request.contract?.professional?.email;
    const clientPhone = request.contract?.client?.phone;
    const professionalPhone = request.contract?.professional?.phone;

    const contactInfo = `
Client Contact:
- Name: ${request.contract?.client ? `${request.contract.client.firstName || ""} ${request.contract.client.lastName || ""}`.trim() : "N/A"}
- Email: ${clientEmail || "N/A"}
- Phone: ${clientPhone || "N/A"}

Professional Contact:
- Name: ${request.contract?.professional ? `${request.contract.professional.firstName || ""} ${request.contract.professional.lastName || ""}`.trim() : "N/A"}
- Email: ${professionalEmail || "N/A"}
- Phone: ${professionalPhone || "N/A"}

Dispute ID: #${request.id}
Contract: ${request.contract?.title || "N/A"}
Amount: $${request.amount?.toFixed(2) || "0.00"}
    `.trim();

    // Copy to clipboard
    navigator.clipboard.writeText(contactInfo).then(() => {
      GlobalService.showToast("success", "Success", "Contact information copied to clipboard!");
    }).catch(() => {
      // Fallback: Show alert with contact info
      alert(contactInfo);
    });
  };

  // Calculate stats
  const stats = useMemo(() => {
    const totalRequests = pagination.totalCount || 0;
    const pendingRequests = refundRequests.filter(request => Number(request.status?.id) === RefundRequestStatusEnum.Pending).length;
    const resolvedRequests = refundRequests.filter(request => Number(request.status?.id) === RefundRequestStatusEnum.Completed).length;
    const totalAmount = refundRequests.reduce((sum, request) => sum + (request.amount || 0), 0);
    const approvedRequests = refundRequests.filter(request => Number(request.status?.id) === RefundRequestStatusEnum.Approved).length;

    return {
      totalRequests,
      pendingRequests,
      resolvedRequests,
      totalAmount,
      escalatedRequests: approvedRequests
    };
  }, [refundRequests, pagination.totalCount]);

  // Get status badge styling
  const getStatusBadge = (statusId) => {
    const numericId = Number(statusId);
    const styles = {
      [RefundRequestStatusEnum.Active]: "bg-blue-100 text-blue-700",
      [RefundRequestStatusEnum.In_Progress]: "bg-blue-100 text-blue-700",
      [RefundRequestStatusEnum.Accepted]: "bg-green-100 text-green-700",
      [RefundRequestStatusEnum.Approved]: "bg-green-100 text-green-700",
      [RefundRequestStatusEnum.Completed]: "bg-green-100 text-green-700",
      [RefundRequestStatusEnum.Cancelled]: "bg-gray-100 text-gray-700",
      [RefundRequestStatusEnum.Refunded]: "bg-purple-100 text-purple-700",
      [RefundRequestStatusEnum.Rejected]: "bg-red-100 text-red-700",
      [RefundRequestStatusEnum.Pending]: "bg-yellow-100 text-yellow-700",
      [RefundRequestStatusEnum.Failed]: "bg-red-100 text-red-700",
      [RefundRequestStatusEnum.Expired]: "bg-gray-100 text-gray-700",
      [RefundRequestStatusEnum.Deleted]: "bg-gray-100 text-gray-700"
    };
    return styles[numericId] || "bg-gray-100 text-gray-700";
  };

  // Get status display name
  const getStatusDisplayName = (statusId) => {
    const numericId = Number(statusId);
    const statuses = {
      [RefundRequestStatusEnum.Active]: "Active",
      [RefundRequestStatusEnum.In_Progress]: "In Progress",
      [RefundRequestStatusEnum.Accepted]: "Accepted",
      [RefundRequestStatusEnum.Approved]: "Approved",
      [RefundRequestStatusEnum.Completed]: "Completed",
      [RefundRequestStatusEnum.Cancelled]: "Cancelled",
      [RefundRequestStatusEnum.Refunded]: "Refunded",
      [RefundRequestStatusEnum.Rejected]: "Rejected",
      [RefundRequestStatusEnum.Pending]: "Pending",
      [RefundRequestStatusEnum.Failed]: "Failed",
      [RefundRequestStatusEnum.Expired]: "Expired",
      [RefundRequestStatusEnum.Deleted]: "Deleted"
    };
    return statuses[numericId] || "Unknown";
  };

  // Calculate withdrawal amount after commission deduction
  const calculateWithdrawlAmount = (amount: number, contract: any): number => {
    try {
      // Get the category type from the contract hierarchy: Contract -> Offer -> Job -> Category -> CategoryType
      const categoryTypeId = contract?.offer?.job?.categoryId?.type?.id;
      const isEmergency = contract?.offer?.job?.isEmergency;
      
      console.log("categoryTypeId", categoryTypeId, contract, isEmergency);
      
      if (!categoryTypeId) {
        console.warn("No category type found in contract hierarchy");
        return amount; // Return original amount if no category type found
      }
      
      console.log("commissionRules", commissionRules);
      
      // Find active commission rule for this category type
      const activeCommission = commissionRules.find((rule: any) => {
        console.log("rule", rule?.status?.id, rule?.categoryType?.id, categoryTypeId, CommissionStatusEnum.Active);
        return Number(rule?.status?.id) === CommissionStatusEnum.Active && // Active status
          Number(rule?.categoryType?.id) === (isEmergency ? CommissionCategoryTypeEnum.Emergency_Service : Number(categoryTypeId));
      });
      
      console.log("activeCommission", activeCommission);
      
      if (!activeCommission) {
        console.warn("No active commission rule found for category type:", categoryTypeId);
        return amount; // Return original amount if no commission rule found
      }

      // Calculate withdrawal amount: amount - (amount * baseRate%)
      const baseRate = activeCommission.baseRate || 0;
      const commissionAmount = amount * (baseRate / 100);
      const withdrawlAmount = amount - commissionAmount;

      console.log("Commission calculation:", {
        amount,
        baseRate,
        commissionAmount,
        withdrawlAmount,
        categoryTypeId,
        commissionRule: activeCommission.name
      });

      return withdrawlAmount;
    } catch (error) {
      console.error("Error calculating withdrawal amount:", error);
      return amount; // Return original amount on error
    }
  };

  // Dispute Request Details Modal Component
  const DisputeRequestDetailsModal = ({ request, isOpen, onClose }) => {
    const [clientPercentage, setClientPercentage] = useState(100);
    const [professionalPercentage, setProfessionalPercentage] = useState(0);
    const [isProcessing, setIsProcessing] = useState(false);

    if (!request || !isOpen) return null;

    // Handle percentage changes
    const handleClientPercentageChange = (value: number) => {
      const clampedValue = Math.max(0, Math.min(100, value));
      setClientPercentage(clampedValue);
      setProfessionalPercentage(100 - clampedValue);
    };

    const handleProfessionalPercentageChange = (value: number) => {
      const clampedValue = Math.max(0, Math.min(100, value));
      setProfessionalPercentage(clampedValue);
      setClientPercentage(100 - clampedValue);
    };

    // Handle refund payment with loading state
    const handleRefundWithLoading = async () => {
      setIsProcessing(true);
      try {
        await handleRefundPayment(request);
      } finally {
        setIsProcessing(false);
      }
    };

    // Handle percentage-based resolution
    const handlePercentageBasedResolution = async () => {
      if (clientPercentage + professionalPercentage !== 100) {
        GlobalService.showToast("error", "Error", "Percentages must add up to 100%");
        return;
      }

      if (!window.confirm(
        "Are you sure you want to resolve this dispute?\n\n" +
        `Client will receive: ${clientPercentage}% ($${((request.amount * clientPercentage) / 100).toFixed(2)})\n` +
        `Professional will receive: ${professionalPercentage}% ($${((request.amount * professionalPercentage) / 100).toFixed(2)})`
      )) {
        return;
      }

      setIsProcessing(true);
      try {
        const contractId = request.contract?.id;
        if (!contractId) {
          GlobalService.showToast("error", "Error", "Contract ID not found for this refund request.");
          return;
        }

        // Get all escrows for this contract
        const escrows = await escrowAPI.getEscrowsByContract(contractId);

        // Filter for funded escrows only
        const fundedEscrows = escrows.filter(
          (escrow) => Number(escrow.status?.id) === EscrowStatusEnum.funded
        );

        if (fundedEscrows.length === 0) {
          GlobalService.showToast("warning", "Warning", "No funded escrows found for this contract.");
          return;
        }

        // Calculate total funded amount
        const totalFundedAmount = fundedEscrows.reduce((sum, escrow) => sum + (escrow.amount || 0), 0);
        const clientRefundAmount = (totalFundedAmount * clientPercentage) / 100;
        const professionalReleaseAmount = (totalFundedAmount * professionalPercentage) / 100;

        // Update all funded escrows to disputed status
        const updatePromises = fundedEscrows.map((escrow) =>
          escrowAPI.updateEscrow(escrow.id, { status: "disputed" })
        );
        await Promise.all(updatePromises);

        const clientId = request.contract?.client?.id;
        const professionalId = request.contract?.professional?.id;

        // Scenario 1: 100% to client
        if (clientPercentage === 100) {
          // Create new escrow with client ID and status refunded
          const clientEscrow = await escrowAPI.createEscrow({
            contractId: contractId,
            clientId: clientId,
            amount: clientRefundAmount,
            currency: fundedEscrows[0]?.currency?.id?.toString() || "1",
            status: "refunded",
            notes: `Full refund to client - Dispute #${request.id}`,
            metadata: {
              disputeId: request.id,
              resolutionType: "full_refund",
              originalEscrows: fundedEscrows.map(e => e.id)
            }
          });
          await escrowAPI.updateEscrow(clientEscrow.id, { status: "refunded" });

          // Process refund for all funded escrows
          const refundPromises = fundedEscrows.map((escrow) =>
            escrowAPI.refundPayment(escrow.id)
          );
          await Promise.all(refundPromises);

          GlobalService.showToast(
            "success",
            "Success",
            `Successfully refunded 100% ($${clientRefundAmount.toFixed(2)}) to client.`
          );
        }
        // Scenario 2: Less than 100% to client (split)
        else if (clientPercentage > 0 && clientPercentage < 100) {
          // Create escrow for client refund
          const clientEscrow = await escrowAPI.createEscrow({
            contractId: contractId,
            clientId: clientId,
            amount: clientRefundAmount,
            totalAmount: clientRefundAmount,
            currency: fundedEscrows[0]?.currency?.id?.toString() || "1",
            status: "refunded",
            notes: `Partial refund to client (${clientPercentage}%) - Dispute #${request.id}`,
            metadata: {
              disputeId: request.id,
              resolutionType: "partial_refund",
              percentage: clientPercentage,
              originalEscrows: fundedEscrows.map(e => e.id)
            }
          });
          await escrowAPI.updateEscrow(clientEscrow.id, { status: "refunded" });

          // Calculate withdrawal amount after commission for professional
          const professionalWithdrawlAmount = calculateWithdrawlAmount(professionalReleaseAmount, request.contract);

          // Create escrow for professional release
          const professionalEscrow = await escrowAPI.createEscrow({
            contractId: contractId,
            professionalId: professionalId,
            amount: professionalReleaseAmount,
            withdrawlAmount: professionalWithdrawlAmount,
            currency: fundedEscrows[0]?.currency?.id?.toString() || "1",
            status: "released",
            notes: `Partial release to professional (${professionalPercentage}%) - Dispute #${request.id} (Withdrawal: $${professionalWithdrawlAmount.toFixed(2)} after commission)`,
            metadata: {
              disputeId: request.id,
              resolutionType: "partial_release",
              percentage: professionalPercentage,
              originalEscrows: fundedEscrows.map(e => e.id),
              commissionDeducted: professionalReleaseAmount - professionalWithdrawlAmount
            }
          });
          await escrowAPI.updateEscrow(professionalEscrow.id, { status: "released" });

          // Process partial refund (we can only refund through the original escrows)
          // Since we can't partially refund through Stripe, we'll mark the resolution in the system
          // and handle the actual transfers separately if needed
          
          GlobalService.showToast(
            "success",
            "Success",
            `Successfully resolved dispute: ${clientPercentage}% ($${clientRefundAmount.toFixed(2)}) to client, ${professionalPercentage}% ($${professionalReleaseAmount.toFixed(2)}) to professional (Withdrawal: $${professionalWithdrawlAmount.toFixed(2)} after commission).`
          );
        }
        // Scenario 3: 100% to professional
        else if (clientPercentage === 0) {
          // Calculate withdrawal amount after commission
          const withdrawlAmount = calculateWithdrawlAmount(professionalReleaseAmount, request.contract);
          
          // Create new escrow with professional and status released
          const professionalEscrow = await escrowAPI.createEscrow({
            contractId: contractId,
            professionalId: professionalId,
            amount: professionalReleaseAmount,
            withdrawlAmount: withdrawlAmount,
            currency: fundedEscrows[0]?.currency?.id?.toString() || "1",
            status: "released",
            notes: `Full release to professional - Dispute #${request.id} (Withdrawal: $${withdrawlAmount.toFixed(2)} after commission)`,
            metadata: {
              disputeId: request.id,
              resolutionType: "full_release",
              originalEscrows: fundedEscrows.map(e => e.id),
              commissionDeducted: professionalReleaseAmount - withdrawlAmount
            }
          });
          await escrowAPI.updateEscrow(professionalEscrow.id, { status: "released" });

          GlobalService.showToast(
            "success",
            "Success",
            `Successfully released 100% ($${professionalReleaseAmount.toFixed(2)}) to professional. Withdrawal amount: $${withdrawlAmount.toFixed(2)} after commission.`
          );
        }

        // Send notifications to both parties
        try {
          const contractTitle = request.contract?.title || "Contract";
          
          // Calculate professional withdrawal amount for notification
          const professionalWithdrawalForNotif = professionalPercentage > 0 
            ? calculateWithdrawlAmount(professionalReleaseAmount, request.contract)
            : 0;

          // Notification to client
          if (clientId) {
            await createUserNotificationAction({
              user: { id: clientId },
              type: { id: NotificationTypeEnum["In-App"] },
              priority: { id: NotificationPriorityEnum.High },
              status: { id: NotificationStatusEnum.Unread },
              title: "Dispute Resolved",
              message: `Your dispute for "${contractTitle}" has been resolved. You will receive ${clientPercentage}% ($${clientRefundAmount.toFixed(2)}) refund.`,
              url: "/client/refund-requests"
            });
          }

          // Notification to professional
          if (professionalId && professionalPercentage > 0) {
            await createUserNotificationAction({
              user: { id: professionalId },
              type: { id: NotificationTypeEnum["In-App"] },
              priority: { id: NotificationPriorityEnum.High },
              status: { id: NotificationStatusEnum.Unread },
              title: "Dispute Resolved",
              message: `The dispute for "${contractTitle}" has been resolved. You will receive ${professionalPercentage}% ($${professionalReleaseAmount.toFixed(2)}). Withdrawal amount: $${professionalWithdrawalForNotif.toFixed(2)} after commission.`,
              url: "/refund-requests"
            });
          }

          console.log("Notifications sent successfully to both parties");
        } catch (notificationError) {
          console.error("Error sending notifications:", notificationError);
        }

        // Update refund request status to Completed
        await handleStatusUpdate(request.id, RefundRequestStatusEnum.Completed);
        
        // Close the modal and refresh
        fetchDisputeRequests();
      } catch (error) {
        console.error("Error processing percentage-based resolution:", error);
        GlobalService.showToast("error", "Error", "Failed to process dispute resolution: " + (error?.message || "Unknown error"));
      } finally {
        setIsProcessing(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" style={{ textAlign: "left" }}>
        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b sticky top-0 bg-white">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-3">
                  <Badge variant={undefined} className={getStatusBadge(request.status?.id)}>
                    {getStatusDisplayName(request.status?.id).toUpperCase()}
                  </Badge>
                </div>
                <h2 className="text-xl font-semibold mt-2">Dispute Request #{request.id}</h2>
                <p className="text-sm text-gray-500">
                  Created: {new Date(request.createdAt).toLocaleString()}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Request Information */}
            <div className="space-y-4">
              <h3 className="font-medium">Request Information</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-500">Request ID</span>
                    <p className="font-medium">{request.id}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Contract ID</span>
                    <p className="font-medium">{request.contract?.id || "N/A"}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Contract Title</span>
                    <p className="font-medium">{request.contract?.title || "No title"}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Amount</span>
                    <p className="font-medium">${request.amount?.toFixed(2) || "0.00"}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Priority</span>
                    <p className="font-medium">{request.priority?.name || "Normal"}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Customer & Professional Information */}
            <div className="space-y-4">
              <h3 className="font-medium">Parties Involved</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Customer Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Customer</h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm text-gray-500">Name</span>
                      <p className="font-medium">
                        {request.contract?.client ?
                          `${request.contract.client.firstName || ""} ${request.contract.client.lastName || ""}`.trim() || "N/A"
                          : "N/A"
                        }
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Email</span>
                      <p className="font-medium">{request.contract?.client?.email || "N/A"}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Phone</span>
                      <p className="font-medium">{request.contract?.client?.phone || "N/A"}</p>
                    </div>
                  </div>
                </div>

                {/* Professional Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Professional</h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm text-gray-500">Name</span>
                      <p className="font-medium">
                        {request.contract?.professional ?
                          `${request.contract.professional.firstName || ""} ${request.contract.professional.lastName || ""}`.trim() || "N/A"
                          : "N/A"
                        }
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Email</span>
                      <p className="font-medium">{request.contract?.professional?.email || "N/A"}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Phone</span>
                      <p className="font-medium">{request.contract?.professional?.phone || "N/A"}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Request Details */}
            <div className="space-y-4">
              <h3 className="font-medium">Request Details</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <div>
                  <span className="text-sm text-gray-500">Reason</span>
                  <p className="mt-1">{request.reason || "No reason provided"}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Description</span>
                  <p className="mt-1">{request.description || "No description provided"}</p>
                </div>
                {request.resolution && (
                  <div>
                    <span className="text-sm text-gray-500">Resolution</span>
                    <p className="mt-1">{request.resolution}</p>
                  </div>
                )}
                {request.rejectReason && (
                  <div>
                    <span className="text-sm text-gray-500">Rejection Reason</span>
                    <p className="mt-1">{request.rejectReason}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Timestamps */}
            <div className="space-y-4">
              <h3 className="font-medium">Timeline</h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-500">Created</span>
                    <p className="font-medium">
                      {new Date(request.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Last Updated</span>
                    <p className="font-medium">
                      {new Date(request.updatedAt).toLocaleString()}
                    </p>
                  </div>
                  {request.resolvedAt && (
                    <div>
                      <span className="text-sm text-gray-500">Resolved</span>
                      <p className="font-medium">
                        {new Date(request.resolvedAt).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Payment Actions - Show when status is Accepted */}
            {Number(request.status?.id) === RefundRequestStatusEnum.Approved && (
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium">Payment Actions</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    className="flex items-center justify-center gap-2 text-purple-600 border-purple-600 hover:bg-purple-50 w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleRefundWithLoading}
                    disabled={isProcessing}
                  >
                    {isProcessing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    {isProcessing ? "Processing..." : "Refund Payment to Client"}
                  </Button>
                  {/* <Button
                    variant="outline"
                    className="flex items-center justify-center gap-2 text-green-600 border-green-600 hover:bg-green-50 w-full"
                    onClick={() => handleReleasePayment(request)}
                  >
                    <Send className="h-4 w-4" />
                    Release Payment
                  </Button> */}
                </div>
              </div>
            )}

            {/* Percentage-Based Resolution - Show when status is Rejected */}
            {Number(request.status?.id) === RefundRequestStatusEnum.Rejected && (
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-medium">Percentage-Based Resolution</h3>
                
                <div className="bg-blue-50 rounded-lg p-4 space-y-4">
                  <p className="text-sm text-gray-600">
                    Resolve dispute by specifying custom percentages for client refund and professional release.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Client Refund Percentage */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Client Refund %
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={clientPercentage}
                          onChange={(e) => handleClientPercentageChange(Number(e.target.value))}
                          disabled={isProcessing}
                          className="flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={clientPercentage}
                          onChange={(e) => handleClientPercentageChange(Number(e.target.value))}
                          disabled={isProcessing}
                          className="w-20 px-3 py-2 border rounded-lg text-center disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </div>
                      <div className="text-sm text-gray-600">
                        Amount: ${((request.amount * clientPercentage) / 100).toFixed(2)}
                      </div>
                    </div>

                    {/* Professional Release Percentage */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Professional Release %
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={professionalPercentage}
                          onChange={(e) => handleProfessionalPercentageChange(Number(e.target.value))}
                          disabled={isProcessing}
                          className="flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={professionalPercentage}
                          onChange={(e) => handleProfessionalPercentageChange(Number(e.target.value))}
                          disabled={isProcessing}
                          className="w-20 px-3 py-2 border rounded-lg text-center disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </div>
                      <div className="text-sm text-gray-600">
                        Amount: ${((request.amount * professionalPercentage) / 100).toFixed(2)}
                      </div>
                    </div>
                  </div>

                  {/* Total Validation */}
                  <div className={`p-3 rounded-lg ${clientPercentage + professionalPercentage === 100 ? "bg-green-50" : "bg-red-50"}`}>
                    <p className={`text-sm font-medium ${clientPercentage + professionalPercentage === 100 ? "text-green-700" : "text-red-700"}`}>
                      Total: {clientPercentage + professionalPercentage}%
                      {clientPercentage + professionalPercentage !== 100 && " (Must equal 100%)"}
                    </p>
                  </div>

                  {/* Quick Presets */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Quick Presets:
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setClientPercentage(100);
                          setProfessionalPercentage(0);
                        }}
                        disabled={isProcessing}
                        className="text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        100% Client
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setClientPercentage(75);
                          setProfessionalPercentage(25);
                        }}
                        disabled={isProcessing}
                        className="text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        75/25
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setClientPercentage(50);
                          setProfessionalPercentage(50);
                        }}
                        disabled={isProcessing}
                        className="text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        50/50
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setClientPercentage(25);
                          setProfessionalPercentage(75);
                        }}
                        disabled={isProcessing}
                        className="text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        25/75
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setClientPercentage(0);
                          setProfessionalPercentage(100);
                        }}
                        disabled={isProcessing}
                        className="text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        100% Professional
                      </Button>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    onClick={handlePercentageBasedResolution}
                    disabled={clientPercentage + professionalPercentage !== 100 || isProcessing}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Processing Resolution...
                      </span>
                    ) : (
                      "Process Resolution"
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Admin Actions */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-medium">Admin Actions</h3>
              <div className="flex flex-wrap gap-3">
                {Number(request.status?.id) === RefundRequestStatusEnum.Pending && (
                  <>
                    <Button
                      variant="outline"
                      className="flex items-center gap-2 text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => handleStatusUpdate(request.id, RefundRequestStatusEnum.In_Progress)}
                      disabled={isProcessing}
                    >
                      <Eye className="h-4 w-4" />
                      Mark as In Progress
                    </Button>
                    <Button
                      variant="outline"
                      className="flex items-center gap-2 text-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => handleStatusUpdate(request.id, RefundRequestStatusEnum.Approved)}
                      disabled={isProcessing}
                    >
                      <CheckCircle className="h-4 w-4" />
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      className="flex items-center gap-2 text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => handleStatusUpdate(request.id, RefundRequestStatusEnum.Rejected)}
                      disabled={isProcessing}
                    >
                      <XCircle className="h-4 w-4" />
                      Reject
                    </Button>
                  </>
                )}
                {Number(request.status?.id) === RefundRequestStatusEnum.In_Progress && (
                  <>
                    <Button
                      variant="outline"
                      className="flex items-center gap-2 text-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => handleStatusUpdate(request.id, RefundRequestStatusEnum.Approved)}
                      disabled={isProcessing}
                    >
                      <CheckCircle className="h-4 w-4" />
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      className="flex items-center gap-2 text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => handleStatusUpdate(request.id, RefundRequestStatusEnum.Rejected)}
                      disabled={isProcessing}
                    >
                      <XCircle className="h-4 w-4" />
                      Reject
                    </Button>
                    <Button
                      variant="outline"
                      className="flex items-center gap-2 text-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => handleStatusUpdate(request.id, RefundRequestStatusEnum.Completed)}
                      disabled={isProcessing}
                    >
                      <CheckCircle className="h-4 w-4" />
                      Mark as Completed
                    </Button>
                  </>
                )}
                {Number(request.status?.id) === RefundRequestStatusEnum.Approved && (
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 text-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => handleStatusUpdate(request.id, RefundRequestStatusEnum.Refunded)}
                    disabled={isProcessing}
                  >
                    <CheckCircle className="h-4 w-4" />
                    Mark as Refunded
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="flex items-center gap-2 text-red-600 border-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => handleDeleteRequest(request.id)}
                  disabled={isProcessing}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete Request
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
      <AdminHeader />

      <main className="flex-1 lg:pl-64 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Dispute Requests</h1>
                <p className="text-gray-600 mt-1">Manage and resolve customer dispute requests</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Export Requests
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Total Requests</p>
                    <p className="text-2xl font-semibold">{stats.totalRequests.toLocaleString()}</p>
                    <p className="text-xs text-gray-400">All time disputes</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Pending</p>
                    <p className="text-2xl font-semibold">{stats.pendingRequests.toLocaleString()}</p>
                    <p className="text-xs text-gray-400">Awaiting review</p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Clock className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Resolved</p>
                    <p className="text-2xl font-semibold">{stats.resolvedRequests.toLocaleString()}</p>
                    <p className="text-xs text-gray-400">Successfully closed</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Total Amount</p>
                    <p className="text-2xl font-semibold">${stats.totalAmount.toLocaleString()}</p>
                    <p className="text-xs text-gray-400">In dispute</p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <DollarSign className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Escalated</p>
                    <p className="text-2xl font-semibold">{stats.escalatedRequests.toLocaleString()}</p>
                    <p className="text-xs text-gray-400">High priority</p>
                  </div>
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-orange-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Search and Filters */}
            <Card className="p-4">
              <div className="flex flex-col lg:flex-row justify-between gap-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full lg:w-auto">
                  <div className="relative flex-1 w-full sm:w-[300px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search dispute requests..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center gap-2 w-full sm:w-auto justify-center"
                  >
                    <Filter className="h-5 w-5" />
                    Filters
                  </Button>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch gap-4 w-full lg:w-auto">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-2 border rounded-lg w-full sm:w-auto"
                  >
                    <option value="all">All Status</option>
                    <option value={RefundRequestStatusEnum.Active}>Active</option>
                    <option value={RefundRequestStatusEnum.In_Progress}>In Progress</option>
                    <option value={RefundRequestStatusEnum.Accepted}>Accepted</option>
                    <option value={RefundRequestStatusEnum.Approved}>Approved</option>
                    <option value={RefundRequestStatusEnum.Completed}>Completed</option>
                    <option value={RefundRequestStatusEnum.Cancelled}>Cancelled</option>
                    <option value={RefundRequestStatusEnum.Refunded}>Refunded</option>
                    <option value={RefundRequestStatusEnum.Rejected}>Rejected</option>
                    <option value={RefundRequestStatusEnum.Pending}>Pending</option>
                    <option value={RefundRequestStatusEnum.Failed}>Failed</option>
                    <option value={RefundRequestStatusEnum.Expired}>Expired</option>
                    <option value={RefundRequestStatusEnum.Deleted}>Deleted</option>
                  </select>


                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-4 py-2 border rounded-lg w-full sm:w-auto"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="amount_high">Highest Amount</option>
                    <option value="amount_low">Lowest Amount</option>
                  </select>
                </div>
              </div>

              {/* Advanced Filters */}
              {showFilters && (
                <div className="mt-4 pt-4 border-t">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date Range
                      </label>
                      <select
                        value={filters.dateRange}
                        onChange={(e) => handleFilterChange("dateRange", e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="all">All Time</option>
                        <option value="today">Today</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Amount Range
                      </label>
                      <select
                        value={filters.amountRange}
                        onChange={(e) => handleFilterChange("amountRange", e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="all">All Amounts</option>
                        <option value="0-100">{getDynamicCurrencySymbol()}0 - {getDynamicCurrencySymbol()}100</option>
                        <option value="100-500">{getDynamicCurrencySymbol()}100 - {getDynamicCurrencySymbol()}500</option>
                        <option value="500-1000">{getDynamicCurrencySymbol()}500 - {getDynamicCurrencySymbol()}1,000</option>
                        <option value="1000+">{getDynamicCurrencySymbol()}1,000+</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </Card>

            {/* Dispute Requests Table */}
            <Card className="overflow-hidden">
              <div className="overflow-x-auto" style={{ textAlign: "left" }}>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedRequests(refundRequests.map(request => request.id));
                            } else {
                              setSelectedRequests([]);
                            }
                          }}
                        />
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Request Info
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Professional
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan={9} className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <span className="ml-2">Loading dispute requests...</span>
                          </div>
                        </td>
                      </tr>
                    ) : refundRequests.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                          No dispute requests found
                        </td>
                      </tr>
                    ) : (
                      refundRequests.map((request) => (
                        <tr key={request.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              checked={selectedRequests.includes(request.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedRequests([...selectedRequests, request.id]);
                                } else {
                                  setSelectedRequests(selectedRequests.filter(id => id !== request.id));
                                }
                              }}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div>
                                <div className="font-medium text-gray-900">#{request.id}</div>
                                <div className="text-sm text-gray-600 mt-1">
                                  {request.contract?.title || "No title"}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {request.contract?.client ?
                                `${request.contract.client.firstName || ""} ${request.contract.client.lastName || ""}`.trim() || "N/A"
                                : "N/A"
                              }
                            </div>
                            <div className="text-sm text-gray-500">
                              {request.contract?.client?.email || ""}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {request.contract?.professional ?
                                `${request.contract.professional.firstName || ""} ${request.contract.professional.lastName || ""}`.trim() || "N/A"
                                : "N/A"
                              }
                            </div>
                            <div className="text-sm text-gray-500">
                              {request.contract?.professional?.email || ""}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge variant={undefined} className={getStatusBadge(request.status?.id)}>
                              {getStatusDisplayName(request.status?.id)}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            ${request.amount?.toFixed(2) || "0.00"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(request.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowRequestDetails(request)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              View Details
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-6 py-4 border-t flex items-center justify-between" style={{ textAlign: "left" }}>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">
                    Showing {refundRequests.length} of {pagination.totalCount} requests
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!pagination.hasNextPage}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </main>

      {/* Dispute Request Details Modal */}
      <DisputeRequestDetailsModal
        request={showRequestDetails}
        isOpen={!!showRequestDetails}
        onClose={() => setShowRequestDetails(null)}
      />
    </div>
  );
};

export default DisputeRequestsPage;
